import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Mic, MicOff } from 'lucide-react';
import './MyCoach.css';
import { LLM_CONFIG } from '../config/llm-config';
import { useGlucose } from '../context/GlucoseContext';
import { useUser } from '../context/UserContext';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'coach';
  timestamp: Date;
}

const MyCoach: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Hello! I'm your health coach. I'm here to help you manage your diabetes, answer questions, and support you on your health journey. How can I assist you today?",
      sender: 'coach',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { addReading, addA1cReading } = useGlucose();
  const { userData } = useUser();
  const apiKey = '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';
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    let nextId = Date.now();
    const currentInput = inputValue;
    const userMessage: Message = {
      id: nextId++,
      text: currentInput,
      sender: 'user',
      timestamp: new Date(),
    };

    setInputValue('');
    setIsTyping(true);

    // AI Biomarker Extraction Logic
    const glucoseMatch = currentInput.match(/(?:glucose|fasting glucose)\D+(\d{2,3})/i);
    const a1cMatch = currentInput.match(/(?:a1c|hba1c)\D+(\d\.\d)/i);

    let extractedGlucose = glucoseMatch ? parseInt(glucoseMatch[1]) : null;
    let extractedA1c = a1cMatch ? parseFloat(a1cMatch[1]) : null;

    let initialMessages = [...messages, userMessage];
    
    if (extractedGlucose || extractedA1c) {
      const now = new Date();
      if (extractedGlucose) {
        addReading({
          value: extractedGlucose,
          type: 'fasting',
          time: now.toTimeString().substring(0, 5),
          date: now.toISOString().split('T')[0]
        });
      }
      if (extractedA1c && addA1cReading) {
        addA1cReading({
          value: extractedA1c,
          date: now.toISOString().split('T')[0]
        });
      }

      const savedDocs = localStorage.getItem('myDocuments');
      let documents = [];
      if (savedDocs) {
        try { documents = JSON.parse(savedDocs); } catch(e) {}
      } else if (userData && userData.documents) {
        documents = [...userData.documents];
      }

      const newDocument = {
        id: Math.max(...documents.map((d: any) => d.id), 0) + 1,
        title: 'Chat Logged Record',
        type: 'bloodwork',
        date: now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        size: 'N/A',
        provider: 'My Health Coach',
        fileName: 'Chat Extraction',
        fileType: 'record',
        healthcareData: {
          glucose: extractedGlucose || undefined,
          a1c: extractedA1c || undefined
        }
      };

      documents.unshift(newDocument);
      localStorage.setItem('myDocuments', JSON.stringify(documents));

      const parts = [];
      if (extractedGlucose) parts.push(`Fasting Glucose (${extractedGlucose} mg/dL)`);
      if (extractedA1c) parts.push(`HbA1c (${extractedA1c}%)`);
      
      const confirmationMsg: Message = {
        id: nextId++,
        text: `I've successfully logged your ${parts.join(' and ')} to your My Records page!`,
        sender: 'coach',
        timestamp: now,
      };

      initialMessages.push(confirmationMsg);
    }
    
    setMessages(initialMessages);

    try {
      const response = await getAIResponse(currentInput, initialMessages);
      const coachResponse: Message = {
        id: nextId++,
        text: response,
        sender: 'coach',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, coachResponse]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorResponse: Message = {
        id: nextId++,
        text: `I'm having trouble connecting right now. Error: ${errorMessage}`,
        sender: 'coach',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const getAIResponse = async (userInput: string, conversationHistory: Message[]): Promise<string> => {
    // Use ChatGPT if API key is provided
    if (apiKey.trim()) {
      const { systemPrompt } = LLM_CONFIG;
      const messages = [
        { role: 'system', content: systemPrompt },
        ...conversationHistory.slice(-10).map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }))
      ];

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages,
          temperature: 0.7,
          max_tokens: 500,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error?.message || response.statusText;
        throw new Error(`ChatGPT API error: ${response.status} - ${errorMsg}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    }

    // Fall back to configured LLM provider
    const { provider, systemPrompt } = LLM_CONFIG;
    
    // Build conversation messages
    const messages = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory.slice(-10).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }))
    ];

    if (provider === 'ollama') {
      const { apiUrl, model } = LLM_CONFIG.ollama;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          messages,
          stream: false,
        }),
      });

      if (!response.ok) {
        throw new Error(`Ollama API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.message.content;
      
    } else if (provider === 'openai') {
      const { apiUrl, apiKey, model } = LLM_CONFIG.openai;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({ model, messages }),
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
      
    } else if (provider === 'lmstudio') {
      const { apiUrl, model } = LLM_CONFIG.lmstudio;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model, messages }),
      });

      if (!response.ok) {
        throw new Error(`LM Studio API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
      
    } else if (provider === 'huggingface') {
      const { apiUrl, apiKey } = LLM_CONFIG.huggingface;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          inputs: `${systemPrompt}\n\nUser: ${userInput}\n\nAssistant:`,
          parameters: {
            max_new_tokens: 250,
            temperature: 0.7,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Hugging Face API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data[0].generated_text.split('Assistant:')[1].trim();
    }

    throw new Error('Invalid LLM provider specified');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const toggleSpeechRecognition = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };


  return (
    <div className="my-coach">
      <div className="coach-header">
        <div className="coach-header-content">
          <div className="coach-avatar">
            <Bot size={24} />
          </div>
          <div className="coach-info">
            <h1 className="coach-title">My Health Coach</h1>
            <p className="coach-status">Online • Always here to help</p>
          </div>
        </div>
      </div>

      <div className="chat-container">
        <div className="messages-list">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message-wrapper ${message.sender === 'user' ? 'user-message' : 'coach-message'}`}
            >
              <div className="message-avatar">
                {message.sender === 'coach' ? (
                  <Bot size={20} />
                ) : (
                  <User size={20} />
                )}
              </div>
              <div className="message-content">
                <div className="message-bubble">
                  <p className="message-text">{message.text}</p>
                </div>
                <span className="message-time">{formatTime(message.timestamp)}</span>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="message-wrapper coach-message">
              <div className="message-avatar">
                <Bot size={20} />
              </div>
              <div className="message-content">
                <div className="message-bubble typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask your health coach anything..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
            />
            <button
              className={`mic-button ${isListening ? 'listening' : ''}`}
              onClick={toggleSpeechRecognition}
              title={isListening ? 'Stop recording' : 'Start voice input'}
            >
              {isListening ? <MicOff size={20} /> : <Mic size={20} />}
            </button>
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCoach;
